import { useState, useRef } from "react";
import { Dropdown, Button, Select, Tooltip } from "antd";
import { CloseOutlined, EllipsisOutlined } from "@ant-design/icons";
import { Icon } from "../../../../components/icon/icon";
import { Chips } from "../../../../components/chips/chips";
import encodeSvgWithColor from "../../../../common/utils/encodeSvgWithColor";
import { useTranslation } from "react-i18next";
import TaskCard from "../../task-management/task-card/task-card";
import VersionPage from "../../task-management/task-card/version-page";
import { useLocation } from "react-router-dom";
import { useProjectNavigate } from "../../../../common/utils/useProjectNavigate";
import ReminderList from "../../../../components/reminder-time/reminder-list";
import { fitActivityDropdownToViewport } from "./activity-dropdown";
import { ActivityControl, ActivityField } from "./activity-field";
import DurationStepper from "./duration-stepper";
import RecurrenceField, { getPeriodicRecurrenceSummary } from "./recurrence-field";
import "./activity-modal.css";

// LF360-1934: a triggered task always locks at the end of its day. The field
// stays on the screen — an admin still needs to know when the task closes — but
// there is nothing to choose, so it holds the one value and is disabled. The
// backend enforces the same, whatever a payload asks for.
const TRIGGERED_LOCK_PERIOD = "endOfDay";
const LOCK_PERIOD_OPTIONS = [
  { label: "End of the day", value: TRIGGERED_LOCK_PERIOD },
];

const TriggeredTaskItem = ({
  task,
  planned,
  activityId,
  deleteTask,
  updateTaskNotificationTime,
  updateTaskDuration,
  updateTaskBaseline,
  updateTaskOffsets,
  duration = 1,
  baselineId = null,
  baselines = [],
  offsets = [],
  style,
  categories,
  readOnly = false,
  notAllowed = false,

  notificationType = "absolute",
  reminders = [],
  updateTaskReminders,
  resetTaskReminders,
  // LF360-1940: whether this activity has been changed at the plan level. False
  // means it follows the task, and the next publish of the task rewrites it.
  remindersCustomised = true,
  triggerSubjectProfileFieldId = null,
  timeProfileFields = [],
  periodicRecurrence = null,
  updateTaskPeriodicRecurrence,
  extraReminders = [],
  updateTaskExtraReminders,
}) => {
  const { t } = useTranslation();
  const { avatar, avatarColor, middleColor, title, backgroundColor, name } = task || {};

  const [showVersionPage, setShowVersionPage] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskMenuOpen, setIsTaskMenuOpen] = useState(false);

  const popupContainerRef = useRef(null);

  const openDrawer = () => {
    setIsTaskMenuOpen(false);
    setSelectedTask(task);
    setIsOpen(true);
  };

  const closeDrawer = () => {
    setIsOpen(false);
    setSelectedTask(null);
  };

  // What the activity's own chip on the plan says. The panel spells the same
  // thing out in fields; this is the one-line version beside the task.
  const sortedOffsets = [...offsets].sort((a, b) => a - b);
  const recurrenceSummary = getPeriodicRecurrenceSummary(periodicRecurrence);

  // Not memoised, and deliberately so. It was a useMemo whose dependency list
  // both missed values the body reads — the reminders list among them — and
  // carried five it no longer reads; it stayed correct only because
  // openPeriodicModal is rebuilt every render, so the memo never actually held.
  // Wrapping that in useCallback, the obvious tidy-up, would have frozen the
  // reminders list: an admin adds one and it does not appear. A plain value
  // renders exactly as often as it did and promises nothing it cannot keep.
  const taskContentRender = (() => {
    const popupContainer = () => popupContainerRef.current || document.body;

    return (
      <div className="activity-modal" ref={popupContainerRef}>
        {/* LF360-1881 — the same header the weekly panel carries: the task, a way
            through to it, and Remove behind the menu where the design puts it. */}
        <div className="activity-modal__header">
          <div className="activity-modal__identity">
            {avatar && (
              <div className="activity-modal__avatar" style={{ backgroundColor }}>
                <img src={encodeSvgWithColor(avatar, avatarColor)} alt="" />
              </div>
            )}
            <p className="activity-modal__title">{title || name}</p>
          </div>

          <Button
            className="activity-modal__open"
            icon={<Icon name="arrow-right" />}
            aria-label="Open task details"
            onClick={openDrawer}
          />

          <span className="activity-modal__header-spacer" />

          <div className="activity-modal__actions">
            {!readOnly && (
              <Dropdown
                trigger={["click"]}
                getPopupContainer={popupContainer}
                menu={{
                  items: [
                    {
                      key: "remove",
                      danger: true,
                      label: t("plan_management.task_modal.remove_task"),
                    },
                  ],
                  onClick: ({ key }) => {
                    if (key === "remove") deleteTask(activityId);
                  },
                }}
              >
                <Button
                  className="activity-modal__action"
                  aria-label="Activity actions"
                  icon={<EllipsisOutlined />}
                />
              </Dropdown>
            )}
            <Button
              className="activity-modal__action"
              aria-label="Close"
              icon={<CloseOutlined />}
              onClick={() => setIsTaskMenuOpen(false)}
            />
          </div>
        </div>

        <div className="activity-modal__section">
          <ActivityField label={t("plan_management.task_modal.baseline")}>
            <ActivityControl>
              <Select
                aria-label={t("plan_management.task_modal.baseline")}
                value={baselineId}
                onChange={(value) => updateTaskBaseline(value, activityId)}
                variant="borderless"
                disabled={readOnly}
                getPopupContainer={popupContainer}
              >
                {baselines.map((b) => (
                  <Select.Option key={b.id} value={b.id}>
                    {b.title}
                  </Select.Option>
                ))}
              </Select>
            </ActivityControl>
          </ActivityField>

          {/* LF360-1934: a triggered task always closes at the end of its day.
              Still on screen — an admin needs to know when it closes — with
              nothing to choose. */}
          <ActivityField label="Lock task">
            <ActivityControl>
              <Select
                aria-label="Lock task"
                value={TRIGGERED_LOCK_PERIOD}
                variant="borderless"
                disabled
                options={LOCK_PERIOD_OPTIONS}
                getPopupContainer={popupContainer}
              />
            </ActivityControl>
          </ActivityField>

          <ActivityField label={t("plan_management.task_modal.duration")}>
            <DurationStepper
              value={duration}
              onChange={(value) => updateTaskDuration(value, activityId)}
              disabled={readOnly}
            />
          </ActivityField>
        </div>

        {/* When it comes round. The two shapes are the two the activity has
            always had, and choosing one still clears the other — see
            RecurrenceField. */}
        <div className="activity-modal__section">
          <RecurrenceField
            periodicRecurrence={periodicRecurrence}
            offsets={offsets}
            onChangeRecurrence={(next) =>
              updateTaskPeriodicRecurrence(next, activityId)
            }
            onChangeOffsets={(next) => updateTaskOffsets(next, activityId)}
            readOnly={readOnly}
            getPopupContainer={popupContainer}
          />
        </div>

        <div className="activity-modal__section activity-modal__section--reminders">
          {/* LF360-1830: one list for every reminder. A task has a day, not a
              time of its own — its time IS a reminder, and there can be several.
              A triggered task knows its assignment date ahead of time, so a
              reminder here may also fire BEFORE it, which is the third kind the
              picker offers here and nowhere else. */}
          <ActivityField label={remindersCustomised ? "Reminder" : "Reminder (default)"}>
            <ReminderList
              value={reminders}
              onChange={(next) => updateTaskReminders?.(next, activityId)}
              readOnly={readOnly}
              allowLeadTime
              triggerFields={timeProfileFields.map((field) => ({
                value: field.id,
                label: field.title,
              }))}
              popupContainerRef={popupContainerRef}
            />
            {/* Only where there is something to go back to: an activity still
                following the task has nothing to reset. */}
            {!readOnly && remindersCustomised && (
              <button
                type="button"
                className="activity-reset"
                onClick={() => resetTaskReminders?.(activityId)}
              >
                Reset to default
              </button>
            )}
          </ActivityField>
        </div>
      </div>
    );
  })();

  const navigate = useProjectNavigate();
  const location = useLocation();

  const handleNavigate = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  return showVersionPage ? (
    <div className="version-page-overlay">
      <VersionPage
        task={selectedTask}
        setShowVersionPage={setShowVersionPage}
      />
    </div>
  ) : (
    <>
      <TaskCard
        isOpen={isOpen}
        close={closeDrawer}
        task={selectedTask}
        categories={categories}
        onlyView={true}
        onOpenTask={() => handleNavigate(`/content/tasks/${selectedTask?.id}`)}
        setShowVersionPage={setShowVersionPage}
      />

      <Dropdown
        open={isTaskMenuOpen}
        onOpenChange={setIsTaskMenuOpen}
        popupRender={() => taskContentRender}
        trigger={["click"]}
        rootClassName="plan-activity-dropdown"
        onPopupAlign={fitActivityDropdownToViewport}
        styles={{ root: { width: "max-content", zIndex: 100 } }}
      >
        <Tooltip
          open={notAllowed ? undefined : false}
          classNames={{ root: "plan-activity-not-allowed-tooltip" }}
          title={
            notAllowed
              ? "This task is no associated with the Plan’s cohort."
              : ""
          }
          placement="top"
          arrow={false}
          autoAdjustOverflow
          getPopupContainer={() => document.body}
        >
          <div
            className={notAllowed ? "triggered-item--not-allowed" : undefined}
            style={{
              cursor: "pointer",
              opacity: 1,
              position: "relative",
              borderRadius: 8,
              ...style,
            }}
          >
            <div
              className="triggered-task-color-indicator"
              style={{
                backgroundColor: notAllowed
                  ? "#FF3B30"
                  : (middleColor || avatarColor || backgroundColor || "var(--text-1)"),
              }}
            />

            <Chips
              text={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    lineHeight: 1.2,
                  }}
                >
                  {(sortedOffsets.length > 0 || baselineId || recurrenceSummary) && (
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 250,
                        color: notAllowed ? "#151515" : "#151515",
                      }}
                    >
                      {recurrenceSummary ||
                        <>
                          {sortedOffsets.length > 0 ? `${sortedOffsets.join(", ")} days` : ""}
                          {baselineId && baselines.length > 0
                            ? `${sortedOffsets.length === 0 ? " After" : " after"} ${
                                baselines.find((b) => b.id === baselineId)?.title || ""
                              }`
                            : ""}
                        </>
                      }
                    </span>
                  )}

                  <span
                    style={{
                      fontSize: 13,
                      color: notAllowed ? "#151515" : "#151515",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 250,
                      marginTop: 2,
                    }}
                  >
                    {title || name}
                  </span>
                </div>
              }
              color={avatarColor ?? "#151515"}
              backgroundColor={notAllowed ? "#FFFFFF" : "var(--shade-color)"}
              borderColor={notAllowed ? "#FF3B30" : "var(--border-color)"}
              style={{
                width: "auto",
                maxWidth: 270,
                height: "40px",
                border: notAllowed ? "1px solid #FF3B30" : "none",
                paddingRight: notAllowed && !readOnly ? 20 : undefined,
                cursor: "pointer",
              }}
            />

            {notAllowed && !readOnly && (
              <Button
                type="text"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteTask?.(activityId);
                }}
                style={{
                  position: "absolute",
                  top: "calc(50% - 11px)",
                  right: 0,
                  padding: 0,
                  width: 22,
                  height: 22,
                  background: "transparent",
                  border: "none",
                  boxShadow: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 2,
                  pointerEvents: "auto",
                }}
                icon={<Icon name="delete" color="#FF3B30" />}
              />
            )}
          </div>
        </Tooltip>
      </Dropdown>
    </>
  );
};

export default TriggeredTaskItem;
